import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash, PencilSimple, Check, X, Buildings, ArrowLeft, Lock, Crown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_COLORS = ['#A855F7', '#2563EB', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#14B8A6', '#F97316'];

/**
 * Multi-brand workspace management. Agency plan required for >1 brand.
 * Free/Pro users still see the page (their default workspace) but the
 * "Add brand" button is locked and deep-links to /pricing.
 */
export default function BrandsSettings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState({ brands: [], active_brand_id: null, is_agency: false, max_brands: 1 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: PRESET_COLORS[0] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/brands`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(t('brands.load_fail'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    if (!data.is_agency && data.brands.length >= 1) {
      toast.info(t('brands.agency_feature'));
      navigate('/pricing');
      return;
    }
    setEditing(null);
    setForm({ name: '', color: PRESET_COLORS[data.brands.length % PRESET_COLORS.length] });
    setShowForm(true);
  };

  const startEdit = (b) => {
    setEditing(b);
    setForm({ name: b.name, color: b.color });
    setShowForm(true);
  };

  const submit = async () => {
    const name = form.name.trim();
    if (!name) { toast.error(t('brands.name_required')); return; }
    setBusy(true);
    try {
      if (editing) {
        await axios.patch(`${BACKEND_URL}/api/brands/${editing.brand_id}`, { name, color: form.color }, { withCredentials: true });
        toast.success(t('brands.updated'));
      } else {
        await axios.post(`${BACKEND_URL}/api/brands`, { name, color: form.color }, { withCredentials: true });
        toast.success(t('brands.created'));
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || t('brands.save_fail');
      if (status === 402) {
        toast.error(detail);
        navigate('/pricing');
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const activate = async (brandId) => {
    if (brandId === data.active_brand_id) return;
    try {
      await axios.post(`${BACKEND_URL}/api/brands/${brandId}/activate`, {}, { withCredentials: true });
      window.dispatchEvent(new CustomEvent('brand:changed', { detail: { brand_id: brandId } }));
      toast.success(t('brands.activated'));
      await load();
    } catch {
      toast.error(t('brands.activate_fail'));
    }
  };

  const remove = async (b) => {
    if (b.is_default) { toast.error(t('brands.cant_delete_default')); return; }
    if (!window.confirm(t('brands.confirm_delete', { name: b.name }))) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/brands/${b.brand_id}`, { withCredentials: true });
      toast.success(t('brands.deleted'));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('brands.delete_fail'));
    }
  };

  const canAdd = data.is_agency && data.brands.length < data.max_brands;

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 lg:p-10">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/settings')}
          data-testid="brands-back-btn"
          className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6"
        >
          <ArrowLeft size={16} /> {t('nav.settings', { defaultValue: 'Settings' })}
        </button>

        <header className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-[Outfit] font-bold flex items-center gap-3">
              <Buildings size={30} weight="fill" className="text-[#A855F7]" />
              {t('brands.title')}
            </h1>
            <p className="text-white/60 mt-2 max-w-2xl">
              {t('brands.subtitle')}
            </p>
          </div>
          {data.is_agency ? (
            <span className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#F59E0B] flex items-center gap-1.5">
              <Crown size={12} weight="fill" /> AGENCY
            </span>
          ) : (
            <span className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold bg-white/5 border border-white/10 text-white/50 flex items-center gap-1.5">
              <Lock size={12} weight="bold" /> {t('brands.upgrade_badge')}
            </span>
          )}
        </header>

        {loading ? (
          <div className="text-white/50">{t('brands.loading')}</div>
        ) : (
          <>
            <div className="space-y-3">
              {data.brands.map((b) => (
                <div
                  key={b.brand_id}
                  data-testid={`brand-row-${b.brand_id}`}
                  className={`p-4 border rounded-xl flex items-center gap-4 transition-all ${
                    b.brand_id === data.active_brand_id
                      ? 'bg-[#A855F7]/10 border-[#A855F7]/40'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  <span className="w-6 h-6 rounded-full flex-shrink-0 border border-white/20" style={{ background: b.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {b.name}
                      {b.is_default && <span className="text-[10px] uppercase tracking-widest text-white/40 bg-white/5 px-1.5 py-0.5 rounded">{t('brands.default')}</span>}
                      {b.brand_id === data.active_brand_id && <span className="text-[10px] uppercase tracking-widest text-[#10B981] bg-[#10B981]/10 px-1.5 py-0.5 rounded">{t('brands.active')}</span>}
                    </div>
                    <div className="text-xs text-white/40">{b.slug} · {b.color}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {b.brand_id !== data.active_brand_id && (
                      <button
                        onClick={() => activate(b.brand_id)}
                        data-testid={`brand-activate-${b.brand_id}`}
                        className="px-3 py-1.5 rounded-lg text-xs bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
                      >
                        {t('brands.activate')}
                      </button>
                    )}
                    <button
                      onClick={() => startEdit(b)}
                      data-testid={`brand-edit-${b.brand_id}`}
                      className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10"
                      aria-label="Edit"
                    >
                      <PencilSimple size={16} />
                    </button>
                    {!b.is_default && (
                      <button
                        onClick={() => remove(b)}
                        data-testid={`brand-delete-${b.brand_id}`}
                        className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-red-500/10"
                        aria-label="Delete"
                      >
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={startCreate}
                data-testid="brand-create-btn"
                disabled={!canAdd}
                className={`w-full py-3 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 transition-all ${
                  canAdd
                    ? 'border-[#A855F7]/40 text-[#A855F7] hover:bg-[#A855F7]/5 hover:border-[#A855F7]'
                    : 'border-white/10 text-white/40 cursor-pointer'
                }`}
              >
                {canAdd ? <Plus size={18} /> : <Lock size={16} />}
                {canAdd ? t('brands.add_new') : t('brands.upgrade_add')}
                <span className="text-xs opacity-60">({data.brands.length}/{data.max_brands})</span>
              </button>
            </div>
          </>
        )}

        {/* Create/Edit modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
            <div
              className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="brand-form-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-[Outfit] font-bold">{editing ? t('brands.edit_title') : t('brands.new_title')}</h2>
                <button onClick={() => setShowForm(false)} className="text-white/50 hover:text-white" data-testid="brand-form-close">
                  <X size={20} />
                </button>
              </div>

              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">{t('brands.name')}</label>
              <input
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t('brands.name_ph')}
                maxLength={64}
                data-testid="brand-form-name"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 mb-5 outline-none focus:border-[#A855F7]"
              />

              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">{t('brands.colour')}</label>
              <div className="flex flex-wrap gap-2 mb-6" data-testid="brand-form-colors">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setForm((f) => ({ ...f, color: c }))}
                    className={`w-9 h-9 rounded-full border-2 transition-all ${form.color === c ? 'border-white scale-110' : 'border-white/20 hover:border-white/40'}`}
                    style={{ background: c }}
                    aria-label={c}
                    data-testid={`brand-color-${c.replace('#', '')}`}
                  >
                    {form.color === c && <Check size={14} weight="bold" className="text-white mx-auto" />}
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm"
                  data-testid="brand-form-cancel"
                >
                  {t('brands.cancel')}
                </button>
                <button
                  onClick={submit}
                  disabled={busy || !form.name.trim()}
                  data-testid="brand-form-submit"
                  className="flex-1 py-2 rounded-lg bg-[#A855F7] hover:bg-[#9333EA] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {busy ? t('brands.saving') : editing ? t('brands.save') : t('brands.create')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
